import { useEffect, useState } from "react";
import { useSnapshot } from "valtio";
import { toast } from "sonner";
import {
  adminStore,
  fetchRoles,
  fetchScopes,
  createRole,
  updateRole,
  deleteRole,
  type Role,
  type Scope,
} from "../stores/admin";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { AlertCircle, Plus, Pencil, Trash2 } from "lucide-react";

interface RoleFormData {
  name: string;
  description: string;
  scopes: Scope[];
}

const defaultFormData: RoleFormData = {
  name: "",
  description: "",
  scopes: [],
};

function RoleDialog({
  open,
  onOpenChange,
  role,
  availableScopes,
  onSubmit,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  role: Role | null;
  availableScopes: Scope[];
  onSubmit: (data: RoleFormData) => Promise<void>;
}) {
  const [formData, setFormData] = useState<RoleFormData>(defaultFormData);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (role) {
      setFormData({
        name: role.name,
        description: role.description,
        scopes: role.scopes ?? [],
      });
    } else {
      setFormData(defaultFormData);
    }
  }, [role, open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await onSubmit(formData);
      onOpenChange(false);
    } finally {
      setLoading(false);
    }
  };

  const toggleScope = (scope: Scope) => {
    const hasScope = formData.scopes.some((s) => s.id === scope.id);
    if (hasScope) {
      setFormData({
        ...formData,
        scopes: formData.scopes.filter((s) => s.id !== scope.id),
      });
    } else {
      setFormData({
        ...formData,
        scopes: [...formData.scopes, scope],
      });
    }
  };

  const isEdit = !!role;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit Role" : "Create Role"}</DialogTitle>
          <DialogDescription>
            {isEdit
              ? `Update the configuration for ${role.name}`
              : "Create a new role with specific permissions"}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Role Name</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              placeholder="admin"
              disabled={isEdit}
              required
            />
            {!isEdit && (
              <p className="text-xs text-muted-foreground">
                Use lowercase (e.g., admin, editor, viewer)
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Input
              id="description"
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              placeholder="Full administrative access"
              required
            />
          </div>

          <div className="space-y-2">
            <Label>Permissions</Label>
            <div className="max-h-48 overflow-y-auto rounded-md border p-3 space-y-2">
              {availableScopes.length > 0 ? (
                availableScopes.map((scope) => (
                  <div key={scope.id} className="flex items-center space-x-2">
                    <Checkbox
                      id={scope.id}
                      checked={formData.scopes.some((s) => s.id === scope.id)}
                      onCheckedChange={() => toggleScope(scope)}
                    />
                    <label
                      htmlFor={scope.id}
                      className="flex-1 text-sm cursor-pointer"
                    >
                      <span className="font-medium">{scope.name}</span>
                      {scope.description && (
                        <span className="text-muted-foreground ml-1">
                          - {scope.description}
                        </span>
                      )}
                    </label>
                  </div>
                ))
              ) : (
                <p className="text-sm text-muted-foreground">
                  No scopes available. Create scopes first.
                </p>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? "Saving..." : isEdit ? "Update" : "Create"} Role
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminRoles() {
  const { roles, rolesLoading, scopes, error } = useSnapshot(adminStore);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState<Role | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    fetchRoles();
    fetchScopes();
  }, []);

  const handleCreateRole = async (data: RoleFormData) => {
    try {
      await createRole({
        name: data.name,
        description: data.description,
        scopes: data.scopes,
      });
      toast.success("Role created successfully");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to create role");
      throw e;
    }
  };

  const handleUpdateRole = async (data: RoleFormData) => {
    if (!editingRole) return;
    try {
      await updateRole(editingRole.id, {
        name: data.name,
        description: data.description,
        scopes: data.scopes,
      });
      toast.success("Role updated successfully");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to update role");
      throw e;
    }
  };

  const handleDeleteRole = async () => {
    if (!roleToDelete) return;
    setDeleteLoading(true);
    try {
      await deleteRole(roleToDelete.id);
      toast.success("Role deleted successfully");
      setDeleteDialogOpen(false);
      setRoleToDelete(null);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to delete role");
    } finally {
      setDeleteLoading(false);
    }
  };

  const openEditDialog = (role: Role) => {
    setEditingRole(role);
    setDialogOpen(true);
  };

  const openCreateDialog = () => {
    setEditingRole(null);
    setDialogOpen(true);
  };

  const openDeleteDialog = (role: Role) => {
    setRoleToDelete(role);
    setDeleteDialogOpen(true);
  };

  return (
    <div className="flex h-full flex-col">
      {/* Fixed Header */}
      <header className="shrink-0 border-b bg-background px-4 py-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Roles</h1>
            <p className="mt-2 text-muted-foreground">
              Manage access control roles and permissions
            </p>
          </div>
          <Button onClick={openCreateDialog}>
            <Plus className="h-4 w-4 mr-2" />
            Create Role
          </Button>
        </div>
      </header>

      {/* Scrollable Content */}
      <main className="min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
          {/* Error State */}
          {error && (
            <Alert variant="destructive" className="mb-6">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Roles Table */}
          <Card>
            <CardHeader>
              <CardTitle>All Roles ({roles.length})</CardTitle>
            </CardHeader>
            <CardContent>
              {rolesLoading ? (
                <div className="space-y-2">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <Skeleton key={i} className="h-12" />
                  ))}
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Scopes</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {roles.length > 0 ? (
                      roles.map((role) => (
                        <TableRow key={role.id}>
                          <TableCell className="font-medium">
                            {role.name}
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {role.description || "-"}
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-wrap gap-1">
                              {role.scopes?.slice(0, 3).map((scope) => (
                                <Badge key={scope.id} variant="secondary">
                                  {scope.name}
                                </Badge>
                              ))}
                              {role.scopes && role.scopes.length > 3 && (
                                <Badge variant="outline">
                                  +{role.scopes.length - 3}
                                </Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openEditDialog(role as Role)}
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-destructive"
                                onClick={() => openDeleteDialog(role as Role)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell
                          colSpan={4}
                          className="text-center text-muted-foreground py-8"
                        >
                          No roles found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Role Dialog */}
      <RoleDialog
        open={dialogOpen}
        onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) setEditingRole(null);
        }}
        role={editingRole}
        availableScopes={scopes as Scope[]}
        onSubmit={editingRole ? handleUpdateRole : handleCreateRole}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Delete Role"
        description={`Are you sure you want to delete the role "${roleToDelete?.name}"? This action cannot be undone.`}
        confirmLabel="Delete"
        variant="destructive"
        onConfirm={handleDeleteRole}
        loading={deleteLoading}
      />
    </div>
  );
}
